import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import { useState } from 'react'
import { useCV } from '../hooks/useCV'

const Skills = () => {
  const { skillCategories } = useCV();
  const [activeCategory, setActiveCategory] = useState('frontend')
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  }

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5 }
    }
  }

  // Function to get an appropriate icon for skills
  const getSkillIcon = (skillName: string) => {
    const name = skillName.toLowerCase();
    
    if (name.includes('react')) return '⚛️';
    if (name.includes('node')) return '📦';
    if (name.includes('javascript') || name.includes('js')) return '🟨';
    if (name.includes('typescript') || name.includes('ts')) return '🔷';
    if (name.includes('python')) return '🐍';
    if (name.includes('aws') || name.includes('amazon')) return '☁️';
    if (name.includes('docker')) return '🐳';
    if (name.includes('graphql')) return '📊';
    if (name.includes('mongodb')) return '🍃';
    if (name.includes('postgres') || name.includes('sql')) return '🐘';
    if (name.includes('git')) return '📝';
    if (name.includes('html')) return '🌐';
    if (name.includes('css')) return '🎨';
    if (name.includes('tailwind')) return '💨';
    if (name.includes('next')) return '▲';
    if (name.includes('vue')) return '🟩';
    if (name.includes('angular')) return '🔺';
    
    // Default icon for other skills
    return '🛠️';
  };

  return (
    <section id="skills" className="py-24 bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 rounded-full mb-4">
            My Skills
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400">
            Technical Expertise
          </h2>
          <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            Technologies and tools I work with to build modern, responsive, and user-friendly applications
          </p>
          
          {/* Category tabs */}
          <div className="flex flex-wrap justify-center gap-3 mt-10 mb-12">
            {skillCategories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all shadow-sm
                  ${activeCategory === category.id 
                    ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-md' 
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:shadow-md'
                  }`}
              >
                {category.name}
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Skills grid */}
        {skillCategories
          .filter(category => category.id === activeCategory)
          .map(category => (
            <motion.div
              key={category.id}
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {category.skills.length > 0 ? (
                category.skills.map((skill) => (
                  <motion.div 
                    key={skill.name}
                    variants={itemVariants}
                    className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-md hover:shadow-lg transition-all border border-gray-100 dark:border-gray-700 relative overflow-hidden group"
                  >
                    <div className="flex items-center mb-4">
                      <span className="text-2xl mr-3">{getSkillIcon(skill.name)}</span>
                      <h3 className="text-xl font-semibold">{skill.name}</h3>
                    </div>
                    
                    <div className="mb-4">
                      <div className="h-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${skill.level}%` }}
                          transition={{ duration: 1, delay: 0.2 }}
                          className={`h-full rounded-full ${
                            skill.level >= 80 
                              ? 'bg-gradient-to-r from-green-400 to-green-500' 
                              : skill.level >= 60 
                                ? 'bg-gradient-to-r from-blue-400 to-blue-500'
                                : 'bg-gradient-to-r from-yellow-400 to-yellow-500'
                          }`}
                        />
                      </div>
                      <div className="flex justify-between mt-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                        <span>Proficiency</span>
                        <span>{skill.level}%</span>
                      </div>
                    </div>
                    
                    {/* Additional visual element for hover effect */}
                    <div className="absolute -right-12 -bottom-12 w-24 h-24 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  </motion.div>
                ))
              ) : (
                <motion.p 
                  variants={itemVariants}
                  className="text-center text-gray-500 dark:text-gray-400 col-span-full py-12"
                >
                  No skills listed in this category
                </motion.p>
              )}
            </motion.div>
          ))}
      </div>
    </section>
  )
}

export default Skills 